import { useTranslation } from "react-i18next";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Dropdown,
  Flex,
  Layout,
  MenuProps,
  message,
  Modal,
  Space,
  TableColumnsType,
  Tooltip,
} from "antd";
import { useNavigate } from "react-router-dom";
import { removeUnitBank } from "../../../../../service/library";
import { formatDateToIsoStringHHmm } from "../../../../../utils/converDate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getFirstLetter } from "../../../../../utils/functionUltils";
import TableData from "../../../../../components/table-data/TableData";
import CardData from "../../../../../components/card-data/CardData";
import ResourceCard from "../resource-card/ResourceCard";
import "./my-question-bank.scss";
import { sharePermissionType } from "../../../../../utils/sharePermission";
import { routesConfig } from "../../../../../config/routes";
import { QuestionBankData } from "../../../../../types/question-bank";

type Props = {
  tab: string;
  page: number;
  size: number;
  viewMode: string;
  searchValue: string;
  isLoading?: boolean;
  totalRecords: number;
  dataQuestionBank: any;
  selectedMode?: boolean;
  disableActions?: boolean;
  selectedBank?: QuestionBankData[];
  fetchDataQuestionBank: () => void;
  setIsOpen: (value: boolean) => void;
  setPageSize: (value: number) => void;
  setPageNumber: (value: number) => void;
  getDetailData: (value: number) => void;
  setIsOpenEdit: (value: boolean) => void;
  setSearchValue: (value: string) => void;
  setSelectedBank?: (values: QuestionBankData[]) => void;
};
const MyQuestionBank = (props: Props) => {
  const { t } = useTranslation();
  const {
    tab,
    page,
    size,
    viewMode,
    isLoading,
    totalRecords,
    selectedMode,
    selectedBank,
    disableActions,
    dataQuestionBank,
    setIsOpen,
    setPageSize,
    setPageNumber,
    setIsOpenEdit,
    getDetailData,
    setSelectedBank,
    fetchDataQuestionBank,
  } = props;

  const navigate = useNavigate();
  const [selectedItem, setSelectedItem] = useState<any>();

  // handle delete unit bank
  const handleDeleteUnitBank = async (bankId: number) => {
    try {
      const res = await removeUnitBank(bankId);
      if (res.status === 200) {
        message.success(t("Xóa thành công", "Xóa thành công"));
        fetchDataQuestionBank();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const baseColumns: TableColumnsType<any> = useMemo(
    () => [
      {
        title: t("Số thứ tự", "Số thứ tự"),
        dataIndex: "numericalOrder",
        width: 3,
      },
      {
        title: t("Tên ngân hàng", "Tên ngân hàng"),
        key: "name",
        width: 25,
        render: (_: any, rec) => (
          <div
            className="flex cursor-pointer gap-2 flex-column"
            style={{ wordBreak: "break-all" }}
            onClick={() => {
              selectedMode
                ? setSelectedItem(rec)
                : navigate(
                    `${rec?.id}?name=${encodeURIComponent(
                      rec.name
                    )}&permission=${
                      tab === "2" && rec.accessPermission === "2"
                        ? "view"
                        : "edit"
                    }`
                  );
            }}
          >
            <Tooltip placement="topLeft" title={rec.name}>
              <div className="question-bank-name">{rec.name}</div>
            </Tooltip>
            <Tooltip
              placement="topLeft"
              overlayStyle={{ maxWidth: 360 }}
              title={rec.description}
            >
              <div className="question-bank-description">{rec.description}</div>
            </Tooltip>
          </div>
        ),
      },
      {
        title: t("Chủ sở hữu", "Chủ sở hữu"),
        key: "authors",
        width: 10,
        render: (_: any, rec) => (
          <Flex gap={16} align="center">
            <Avatar
              className="table-avatar"
              src={
                tab === "1"
                  ? rec.authors?.[0]?.imagePath
                  : rec.authors?.[0]?.avatar
              }
              size={33}
            >
              {getFirstLetter(
                tab === "1"
                  ? rec.authors?.[0]?.userName
                  : rec.authors?.[0]?.universityMember
              )}
            </Avatar>
            {tab === "1" ? (
              <div style={{ color: "#676767" }}>{t("tôi", "tôi")}</div>
            ) : (
              <div>
                <div className="font-weight-5" style={{ color: "#676767" }}>
                  {rec.authors?.[0]?.universityMember}
                </div>
                <div style={{ color: "#676767" }}>
                  {rec.authors?.[0]?.userName}
                </div>
              </div>
            )}
          </Flex>
        ),
      },
      {
        title: t("Chỉnh sửa lần cuối", "Chỉnh sửa lần cuối"),
        key: "updateUser",
        width: 10,
        render: (_: any, rec) => (
          <div>
            <Flex gap={16} align="center">
              {tab === "1" ? (
                <div style={{ color: "#676767" }}>
                  {rec.updatedDate
                    ? formatDateToIsoStringHHmm(rec.updatedDate)
                    : null}
                </div>
              ) : (
                <>
                  <Avatar
                    className="table-avatar"
                    src={
                      rec.updateUser
                        ? rec.updateUser?.universityLogo
                        : rec.authors?.[0]?.avatar
                    }
                    size={33}
                  >
                    {getFirstLetter(
                      rec.updateUser
                        ? rec.updateUser?.productionUnitName
                        : rec.authors?.[0]?.universityMember
                    )}
                  </Avatar>
                  <div>
                    <div className="font-weight-5" style={{ color: "#676767" }}>
                      {rec.updateUser
                        ? rec.updateUser.universityName
                        : rec.authors?.[0]?.universityMember}
                    </div>
                    <div style={{ color: "#676767" }}>
                      {rec.updateUser
                        ? rec.updateUser.name
                        : rec.authors?.[0]?.userName}
                    </div>
                    <div style={{ color: "#676767" }}>
                      {rec.updatedDate
                        ? formatDateToIsoStringHHmm(rec.updatedDate)
                        : null}
                    </div>
                  </div>
                </>
              )}
            </Flex>
          </div>
        ),
      },
    ],
    [navigate, tab, t]
  );

  const actionColumn: TableColumnsType<any> = disableActions
    ? []
    : [
        {
          title: t("Thao tác", "Thao tác"),
          key: "actions",
          width: 5,
          render: (_: any, rec) => {
            const items: MenuProps["items"] = [
              {
                key: "3",
                label: (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `/${routesConfig.courseLibrary}/${routesConfig.sharePermission}/${rec.id}?type=${sharePermissionType.QUESTION_BANK}`
                      );
                    }}
                  >
                    <ShareAltOutlined /> {t("Chia sẻ", "Chia sẻ")}
                  </div>
                ),
              },
              {
                key: "1",
                label: (
                  <div
                    onClick={(e) => {
                      setIsOpen(true),
                        setIsOpenEdit(true),
                        getDetailData(rec.id);
                    }}
                  >
                    <EditOutlined /> {t("Chỉnh sửa", "Chỉnh sửa")}{" "}
                  </div>
                ),
              },
              {
                key: "2",
                label: (
                  <div
                    style={{ color: "#ff4d4f" }}
                    onClick={() =>
                      Modal.confirm({
                        title: `Xóa ngân hàng`,
                        content: `Dữ liệu đã xoá sẽ không thể khôi phục. Bạn có chắc chắn muốn xoá?`,
                        onOk: () => handleDeleteUnitBank(rec.id),
                        centered: true,
                        okText: t("Xác nhận", "Xác nhận"),
                        cancelText: t("Huỷ bỏ", "Huỷ bỏ"),
                        footer: (_, { OkBtn, CancelBtn }) => (
                          <>
                            <CancelBtn />
                            <OkBtn />
                          </>
                        ),
                      })
                    }
                  >
                    <DeleteOutlined /> {t("Xóa", "Xóa")}{" "}
                  </div>
                ),
              },
            ];
            return (
              <Space>
                {tab === "2" && rec.accessPermission === "2" ? (
                  <Button
                    onClick={() =>
                      navigate(
                        `${rec?.id}?name=${encodeURIComponent(
                          rec.name
                        )}&permission=${
                          tab === "2" && rec.accessPermission === "2"
                            ? "view"
                            : "edit"
                        }`
                      )
                    }
                  >
                    <EyeOutlined style={{ color: "#757575" }} />
                  </Button>
                ) : (
                  <Dropdown menu={{ items }} className="action-table">
                    <a onClick={(e) => e.preventDefault()}>
                      <MoreOutlined />
                    </a>
                  </Dropdown>
                )}
              </Space>
            );
          },
        },
      ];

  const getColumns = useCallback(
    () => [...baseColumns, ...actionColumn],
    [baseColumns, actionColumn]
  );

  useEffect(() => {
    setSelectedBank && setSelectedBank(selectedItem ? [selectedItem] : []);
  }, [selectedItem]);

  const renderCards = () => (
    <CardData
      data={dataQuestionBank}
      loading={isLoading}
      totalRecords={totalRecords}
      pageNumber={page}
      pageSize={size}
      setPageNumber={setPageNumber}
      setPageSize={setPageSize}
      minHeight="600px"
      modalMode={"select"}
      renderItem={(item: any) => (
        <ResourceCard
          tab={tab}
          key={item.id}
          record={item}
          selectedBank={selectedBank}
          selectedItem={selectedItem}
          selectedMode={selectedMode}
          setSelectedItem={setSelectedItem}
          onEdit={() => {
            setIsOpen(true);
            setIsOpenEdit(true);
            getDetailData(item.id);
          }}
          onDelete={() =>
            Modal.confirm({
              title: `Xóa ngân hàng`,
              content: `Dữ liệu đã xoá sẽ không thể khôi phục. Bạn có chắc chắn muốn xoá?`,
              onOk: () => handleDeleteUnitBank(item.id),
              centered: true,
              okText: t("Xác nhận", "Xác nhận"),
              cancelText: t("Huỷ bỏ", "Huỷ bỏ"),
              footer: (_, { OkBtn, CancelBtn }) => (
                <>
                  <CancelBtn />
                  <OkBtn />
                </>
              ),
            })
          }
          disableActions={tab === "2" && item.accessPermission === "2"}
        />
      )}
    />
  );

  const renderTable = () => (
    <TableData
      dataColumns={selectedMode ? baseColumns : getColumns()}
      dataTable={dataQuestionBank}
      loadingTable={isLoading}
      pageNumber={page}
      pageSize={size}
      totalRecords={totalRecords}
      setPageNumber={setPageNumber}
      setPageSize={setPageSize}
      scroll={selectedMode ? undefined : { x: 1500, y: 570 }}
      hideSelection={selectedMode}
      selectOneRow
      pagination
      rowSelected={selectedBank}
      setSelectedRowKeysProps={setSelectedBank}
      handleTableChangeProps={() => null}
    />
  );

  return (
    <Layout className="page-header-group page-header-question-bank">
      <div
        className={`tabs-heading question-bank-tabs ${
          selectedMode ? "select-mode" : ""
        }`}
      >
        {viewMode === "card" ? renderCards() : renderTable()}
      </div>
    </Layout>
  );
};

export default MyQuestionBank;
